import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import {
    getAccountingEndpoints,
    createAccountingEndpoint,
    updateAccountingEndpoint,
    deleteAccountingEndpoint,
    getAccountingProviders,
    getAccountingDocumentTypes
} from '../../api/common';

// Devuelve un valor primitivo para precargar en inputs HTML, escapando comillas
// dobles que romperian el atributo value="...".
function _attr(v) {
    if (v === null || v === undefined) return '';
    return String(v).replace(/"/g, '&quot;');
}

// Construye las filas de mapeo de path params a partir de los tokens {param}
// encontrados en un path_template dado. Precarga valores si se está editando.
function buildPathParamsRows(pathTemplate, existingParams = []) {
    const tokens = pathTemplate.match(/\{(.+?)\}/g) || [];
    if (!tokens.length) return '';
    return tokens.map((match) => {
        const param = match.replace(/[{}]/g, '');
        const found = existingParams.find(p => p.param === param) || {};
        return `
            <tr>
                <td><code>${match}</code></td>
                <td>
                    <input class="form-control form-control-sm swal-pp-source"
                        data-param="${param}"
                        placeholder="cuentaOrigen.idContable"
                        value="${_attr(found.source || '')}" />
                </td>
                <td>
                    <input class="form-control form-control-sm swal-pp-desc"
                        data-param="${param}"
                        placeholder="ID contable de la cuenta origen"
                        value="${_attr(found.description || '')}" />
                </td>
            </tr>`;
    }).join('');
}

function buildModalHtml(providers = [], documentTypes = [], endpoint = null) {
    const editing = endpoint !== null;
    const activeProviders = providers.filter(p => p.active);

    const editingProviderId = editing
        ? (endpoint.provider_id?._id ? String(endpoint.provider_id._id) : String(endpoint.provider_id))
        : '';

    // response_shape: precargar valores del endpoint editado (o defaults)
    const rs = (editing && endpoint.response_shape) ? endpoint.response_shape : {};
    const rsLeaf = rs.leaf_filter || {};
    const rsPag = rs.pagination || {};

    // El bloque response_shape solo es relevante para endpoints de LECTURA
    // (ej: GET /categories de Alegra). Para POST de comprobantes se ignora.
    // Por eso lo dejamos colapsable.
    const expandShape = editing && (
        rs.root_path || rs.is_tree || rsLeaf.field || rsPag.items_path
    );

    // path_template: precargar si existe en el endpoint editado
    const initialPathTemplate = editing ? (endpoint.path_template || '') : '';
    const initialPathParams   = editing ? (endpoint.path_params   || []) : [];
    const hasTokens = /\{.+?\}/.test(initialPathTemplate);

    return `
        <div class="text-start">
            <div class="mb-2">
                <label class="form-label fw-semibold">Proveedor</label>
                <select id="swal-ep-provider" class="swal2-input">
                    <option value="">-- Selecciona proveedor --</option>
                    ${activeProviders.map(p =>
                        `<option value="${p._id}" ${editing && editingProviderId === String(p._id) ? 'selected' : ''}>${p.display_name}</option>`
                    ).join('')}
                </select>
            </div>
            <div class="mb-2">
                <label class="form-label fw-semibold">Tipo de documento</label>
                <select id="swal-ep-doctype" class="swal2-input">
                    <option value="">-- Selecciona tipo --</option>
                    ${documentTypes.map(dt =>
                        `<option value="${dt.code}" ${editing && endpoint.document_type === dt.code ? 'selected' : ''}>${dt.label}</option>`
                    ).join('')}
                </select>
            </div>
            <div class="mb-2">
                <label class="form-label fw-semibold">Endpoint (debe iniciar con /)</label>
                <input id="swal-ep-endpoint" class="swal2-input" placeholder="/contacts"
                    value="${_attr(editing ? endpoint.endpoint : '')}" required />
            </div>

            <!-- Path Template — para endpoints con parámetros en la URL -->
            <div class="mb-2">
                <label class="form-label fw-semibold">Path Template <small class="text-muted fw-normal">(opcional)</small></label>
                <input id="swal-ep-path-template" class="swal2-input"
                    placeholder="/bank-accounts/{sourceAccountId}/transfer"
                    value="${_attr(initialPathTemplate)}" />
                <small class="text-muted">
                    Usa <code>{'{'}param{'}'}</code> para valores dinámicos en la URL. Dejar vacío si el endpoint es plano.
                </small>
            </div>

            <!-- Mapeo de path params — se muestra solo cuando el template tiene tokens {param} -->
            <div id="swal-ep-path-params-wrap" style="display:${hasTokens ? '' : 'none'}">
                <label class="form-label fw-semibold mb-1">Mapeo de Path Params</label>
                <table class="table table-sm table-bordered mb-2">
                    <thead class="table-dark">
                        <tr>
                            <th>Parámetro</th>
                            <th>Fuente en el modelo</th>
                            <th>Descripción</th>
                        </tr>
                    </thead>
                    <tbody id="swal-ep-path-params-body">
                        ${buildPathParamsRows(initialPathTemplate, initialPathParams)}
                    </tbody>
                </table>
            </div>

            <div class="mb-2 w-100">
                <label class="form-label fw-semibold">Headers (JSON — pre-cargados del proveedor, editable)</label>
                <textarea id="swal-ep-extra-headers" class="swal2-textarea" placeholder='{}'>${editing && endpoint.extra_headers && Object.keys(endpoint.extra_headers).length > 0 ? JSON.stringify(endpoint.extra_headers, null, 2) : ''}</textarea>
            </div>

            <!-- response_shape: configuracion de LECTURA (solo para endpoints inbound como GET cuentas contables) -->
            <details ${expandShape ? 'open' : ''} class="border rounded p-2 mb-2">
                <summary class="fw-semibold" style="cursor:pointer">
                    Forma de la respuesta (response_shape)
                    <small class="text-muted ms-2">solo para endpoints de lectura (ej: GET cuentas contables)</small>
                </summary>

                <div class="row g-2 mt-2">
                    <div class="col-12">
                        <label class="form-label small mb-0">root_path</label>
                        <input id="swal-ep-rs-root" class="swal2-input m-0" placeholder='vacio para array raiz, ej: "data" o "QueryResponse.Account"'
                            value="${_attr(rs.root_path || '')}" />
                    </div>
                    <div class="col-12 d-flex align-items-center gap-2 mt-2">
                        <input type="checkbox" id="swal-ep-rs-istree" class="form-check-input"
                            ${rs.is_tree ? 'checked' : ''} />
                        <label class="form-check-label" for="swal-ep-rs-istree">
                            La respuesta es un arbol recursivo (Alegra)
                        </label>
                    </div>
                    <div class="col-12" id="swal-ep-rs-children-wrap" style="display:${rs.is_tree ? '' : 'none'}">
                        <label class="form-label small mb-0">children_field</label>
                        <input id="swal-ep-rs-children" class="swal2-input m-0" placeholder='children'
                            value="${_attr(rs.children_field || 'children')}" />
                    </div>

                    <div class="col-6">
                        <label class="form-label small mb-0">leaf_filter.field</label>
                        <input id="swal-ep-rs-leaf-field" class="swal2-input m-0" placeholder='ej: "use"'
                            value="${_attr(rsLeaf.field || '')}" />
                    </div>
                    <div class="col-6">
                        <label class="form-label small mb-0">leaf_filter.equals</label>
                        <input id="swal-ep-rs-leaf-eq" class="swal2-input m-0" placeholder='ej: "movement"'
                            value="${_attr(rsLeaf.equals || '')}" />
                    </div>

                    <div class="col-12 mt-2"><small class="text-muted">Paginacion (opcional, solo si el proveedor pagina)</small></div>
                    <div class="col-4">
                        <label class="form-label small mb-0">items_path</label>
                        <input id="swal-ep-rs-pag-items" class="swal2-input m-0" placeholder='data'
                            value="${_attr(rsPag.items_path || '')}" />
                    </div>
                    <div class="col-4">
                        <label class="form-label small mb-0">next_path</label>
                        <input id="swal-ep-rs-pag-next" class="swal2-input m-0" placeholder='next'
                            value="${_attr(rsPag.next_path || '')}" />
                    </div>
                    <div class="col-4">
                        <label class="form-label small mb-0">style</label>
                        <select id="swal-ep-rs-pag-style" class="swal2-input m-0">
                            <option value=""    ${!rsPag.style ? 'selected' : ''}>(sin paginacion)</option>
                            <option value="url" ${rsPag.style === 'url' ? 'selected' : ''}>url</option>
                            <option value="cursor" ${rsPag.style === 'cursor' ? 'selected' : ''}>cursor</option>
                            <option value="page" ${rsPag.style === 'page' ? 'selected' : ''}>page</option>
                        </select>
                    </div>
                </div>
            </details>
        </div>`;
}

export default function EndpointsTab() {
    const [endpoints, setEndpoints] = useState([]);
    const [providers, setProviders] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [filterProvider, setFilterProvider] = useState('');
    const [loading, setLoading] = useState(false);

    const loadProviders = useCallback(async () => {
        try {
            const res = await getAccountingProviders();
            if (res?.data?.success) setProviders(res.data.data);
        } catch { /* ignore */ }
    }, []);

    const loadEndpoints = useCallback(async (provId = '') => {
        setLoading(true);
        try {
            const params = {};
            if (provId) params.provider_id = provId;
            const res = await getAccountingEndpoints(params);
            if (res?.data?.success) setEndpoints(res.data.data);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        loadProviders();
        loadEndpoints();
        (async () => {
            try {
                const res = await getAccountingDocumentTypes();
                if (res?.data?.success) setDocumentTypes(res.data.data);
            } catch { /* ignore */ }
        })();
    }, [loadProviders, loadEndpoints]);

    function handleFilterChange(e) {
        const val = e.target.value;
        setFilterProvider(val);
        loadEndpoints(val);
    }

    async function openModal(endpoint = null) {
        const editing = endpoint !== null;
        const { value: payload } = await Swal.fire({
            title: editing ? 'Editar Endpoint' : 'Nuevo Endpoint',
            html: buildModalHtml(providers, documentTypes, endpoint),
            showCancelButton: true,
            confirmButtonText: 'Guardar',
            cancelButtonText: 'Cancelar',
            width: 560,
            didOpen: () => {
                // Toggle children_field segun is_tree
                const isTreeCb = document.getElementById('swal-ep-rs-istree');
                const childrenWrap = document.getElementById('swal-ep-rs-children-wrap');
                if (isTreeCb && childrenWrap) {
                    isTreeCb.addEventListener('change', () => {
                        childrenWrap.style.display = isTreeCb.checked ? '' : 'none';
                    });
                }

                // Regenerar filas de path params al editar el path_template
                const pathTemplateInput = document.getElementById('swal-ep-path-template');
                const paramsWrap = document.getElementById('swal-ep-path-params-wrap');
                const paramsBody = document.getElementById('swal-ep-path-params-body');
                if (pathTemplateInput && paramsWrap && paramsBody) {
                    pathTemplateInput.addEventListener('input', () => {
                        const tpl = pathTemplateInput.value;
                        const hasTokens = /\{.+?\}/.test(tpl);
                        if (hasTokens) {
                            // Preservar valores ya ingresados antes de re-renderizar
                            const prevSources = {};
                            const prevDescs   = {};
                            paramsBody.querySelectorAll('.swal-pp-source').forEach(el => {
                                prevSources[el.dataset.param] = el.value;
                            });
                            paramsBody.querySelectorAll('.swal-pp-desc').forEach(el => {
                                prevDescs[el.dataset.param] = el.value;
                            });
                            const existingParams = Object.keys(prevSources).map(param => ({
                                param,
                                source: prevSources[param] || '',
                                description: prevDescs[param] || ''
                            }));
                            paramsBody.innerHTML = buildPathParamsRows(tpl, existingParams);
                            paramsWrap.style.display = '';
                        } else {
                            paramsBody.innerHTML = '';
                            paramsWrap.style.display = 'none';
                        }
                    });
                }

                if (editing) return;
                // En modo crear, precargar headers default del proveedor al cambiar selector
                const providerSelect = document.getElementById('swal-ep-provider');
                if (!providerSelect) return;
                function fillHeaders() {
                    const pid = providerSelect.value;
                    const provider = providers.find(p => String(p._id) === pid);
                    const ta = document.getElementById('swal-ep-extra-headers');
                    if (!ta) return;
                    if (provider?.default_headers && Object.keys(provider.default_headers).length > 0) {
                        ta.value = JSON.stringify(provider.default_headers, null, 2);
                    } else {
                        ta.value = '';
                    }
                }
                providerSelect.addEventListener('change', fillHeaders);
            },
            preConfirm: () => {
                const provider_id   = document.getElementById('swal-ep-provider')?.value;
                const document_type = document.getElementById('swal-ep-doctype')?.value;
                const ep            = document.getElementById('swal-ep-endpoint')?.value?.trim();
                const headersRaw    = document.getElementById('swal-ep-extra-headers')?.value?.trim();

                if (!provider_id)   { Swal.showValidationMessage('Selecciona un proveedor'); return false; }
                if (!document_type) { Swal.showValidationMessage('Selecciona un tipo de documento'); return false; }
                if (!ep)            { Swal.showValidationMessage('El endpoint es requerido'); return false; }
                if (!ep.startsWith('/')) { Swal.showValidationMessage('El endpoint debe iniciar con /'); return false; }

                let extra_headers = {};
                if (headersRaw) {
                    try { extra_headers = JSON.parse(headersRaw); }
                    catch { Swal.showValidationMessage('Los headers no son JSON válido'); return false; }
                }

                // response_shape (todo opcional)
                const rsRoot     = document.getElementById('swal-ep-rs-root')?.value?.trim() || '';
                const rsIsTree   = !!document.getElementById('swal-ep-rs-istree')?.checked;
                const rsChildren = document.getElementById('swal-ep-rs-children')?.value?.trim() || 'children';
                const rsLeafF    = document.getElementById('swal-ep-rs-leaf-field')?.value?.trim() || null;
                const rsLeafEq   = document.getElementById('swal-ep-rs-leaf-eq')?.value?.trim() || null;
                const rsPagItems = document.getElementById('swal-ep-rs-pag-items')?.value?.trim() || null;
                const rsPagNext  = document.getElementById('swal-ep-rs-pag-next')?.value?.trim() || null;
                const rsPagStyle = document.getElementById('swal-ep-rs-pag-style')?.value || null;

                const response_shape = {
                    root_path: rsRoot,
                    is_tree: rsIsTree,
                    children_field: rsIsTree ? rsChildren : 'children',
                    leaf_filter: { field: rsLeafF, equals: rsLeafEq },
                    pagination: { items_path: rsPagItems, next_path: rsPagNext, style: rsPagStyle }
                };

                // path_template y path_params
                const path_template = document.getElementById('swal-ep-path-template')?.value?.trim() || null;
                const path_params = [];
                if (path_template) {
                    document.querySelectorAll('#swal-ep-path-params-body .swal-pp-source').forEach(sourceEl => {
                        const param = sourceEl.dataset.param;
                        const descEl = document.querySelector(
                            `#swal-ep-path-params-body .swal-pp-desc[data-param="${param}"]`
                        );
                        path_params.push({
                            param,
                            source: sourceEl.value.trim(),
                            description: descEl ? descEl.value.trim() : ''
                        });
                    });
                }

                return {
                    provider_id,
                    document_type,
                    endpoint: ep,
                    extra_headers,
                    response_shape,
                    path_template: path_template || null,
                    path_params
                };
            }
        });

        if (!payload) return;

        try {
            let res;
            if (editing) {
                res = await updateAccountingEndpoint(endpoint._id, payload);
            } else {
                res = await createAccountingEndpoint(payload);
            }
            if (res?.data?.success) {
                Swal.fire({ icon: 'success', title: editing ? 'Endpoint actualizado' : 'Endpoint creado', timer: 1500, showConfirmButton: false });
                loadEndpoints(filterProvider);
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: res?.data?.error || 'Error al guardar' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error de conexión', text: 'Error de conexión con el servidor' });
        }
    }

    async function handleDelete(endpoint) {
        const dtLabel = documentTypes.find(dt => dt.code === endpoint.document_type)?.label || endpoint.document_type;
        const { isConfirmed } = await Swal.fire({
            title: `¿Eliminar endpoint para '${dtLabel}'?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33'
        });
        if (!isConfirmed) return;

        try {
            const res = await deleteAccountingEndpoint(endpoint._id);
            if (res?.data?.success) {
                Swal.fire({ icon: 'success', title: 'Endpoint eliminado', timer: 1500, showConfirmButton: false });
                loadEndpoints(filterProvider);
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: res?.data?.error || 'No se pudo eliminar' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error de conexión', text: 'Error de conexión con el servidor' });
        }
    }

    return (
        <div>
            <div className="alert alert-info py-2 mb-3" style={{ fontSize: 13 }}>
                <strong>Nota:</strong> Cada proveedor tiene un endpoint por tipo de documento. Los headers se pre-cargan desde los headers por defecto del proveedor y pueden editarse por endpoint.
            </div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-2">
                    <h6 className="mb-0">Endpoints</h6>
                    <select className="form-select form-select-sm" style={{ width: 200 }} value={filterProvider} onChange={handleFilterChange}>
                        <option value="">Todos los proveedores</option>
                        {providers.map(p => (
                            <option key={p._id} value={p._id}>{p.display_name}</option>
                        ))}
                    </select>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => openModal()}>+ Agregar Endpoint</button>
            </div>

            {loading ? (
                <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>Proveedor</th>
                                <th>Tipo de Documento</th>
                                <th>Endpoint</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {endpoints.length === 0 && (
                                <tr><td colSpan={5} className="text-center text-muted py-3">No hay endpoints configurados</td></tr>
                            )}
                            {endpoints.map(ep => {
                                const dtLabel = documentTypes.find(dt => dt.code === ep.document_type)?.label || ep.document_type;
                                const rs = ep.response_shape || {};
                                const tieneShape = rs.is_tree || rs.root_path || rs.leaf_filter?.field;
                                return (
                                    <tr key={ep._id}>
                                        <td>{ep.provider_id?.display_name || ep.provider_id}</td>
                                        <td><span className="badge bg-primary bg-opacity-10 text-primary border border-primary" style={{ fontWeight: 500 }}>{dtLabel}</span></td>
                                        <td>
                                            <code>{ep.endpoint}</code>
                                            {ep.path_template && (
                                                <span
                                                    className="badge bg-warning bg-opacity-25 text-warning border border-warning ms-2"
                                                    style={{ fontSize: 10 }}
                                                    title={`path_template: ${ep.path_template}`}
                                                >
                                                    path params
                                                </span>
                                            )}
                                            {tieneShape && (
                                                <span
                                                    className="badge bg-info bg-opacity-25 text-info border border-info ms-2"
                                                    style={{ fontSize: 10 }}
                                                    title={`response_shape configurado${rs.is_tree ? ' (arbol)' : ''}`}
                                                >
                                                    lectura
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {ep.active
                                                ? <span className="badge bg-success">Activo</span>
                                                : <span className="badge bg-secondary">Inactivo</span>}
                                        </td>
                                        <td>
                                            <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openModal(ep)}>Editar</button>
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(ep)}>Eliminar</button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
